import { Field, TextInput } from "@components/ui/Field"
import { Eye, EyeOff } from "lucide-react"
import { forwardRef, useState, type InputHTMLAttributes } from "react"
import { useTranslation } from "react-i18next"

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "id"> {
  label: string
  hint?: string
  error?: string
  /** Extra ids the input is described by (a rules checklist), joined with the field's own. */
  describedBy?: string
}

/**
 * A labelled password input with its own show/hide toggle, on the Field
 * primitives (PRD-60 chunk A). Shared by the login and the public
 * activation / reset pages; forwards the ref so react-hook-form's `register`
 * works as well as a controlled `value`/`onChange`. The toggle is a 44px
 * target on a phone (it was 32px on the login) and announces its state.
 */
export const PasswordField = forwardRef<HTMLInputElement, Props>(function PasswordField(
  { label, hint, error, describedBy, className, ...props },
  ref,
) {
  const { t } = useTranslation("common")
  const [visible, setVisible] = useState(false)
  const ToggleIcon = visible ? EyeOff : Eye
  return (
    <Field label={label} hint={hint} error={error}>
      {({ id, describedBy: fieldDescribedBy, invalid }) => (
        <div className="relative">
          <TextInput
            ref={ref}
            id={id}
            type={visible ? "text" : "password"}
            aria-invalid={invalid}
            aria-describedby={
              [describedBy, fieldDescribedBy].filter(Boolean).join(" ") || undefined
            }
            className={className ?? "pr-12"}
            {...props}
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? t("password.hide") : t("password.show")}
            aria-pressed={visible}
            className="rounded-control text-ink-subtle hover:bg-inset hover:text-ink absolute top-1/2 right-0.5 flex h-10 w-11 -translate-y-1/2 items-center justify-center transition-colors duration-150 md:h-8 md:w-9"
          >
            <ToggleIcon className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}
    </Field>
  )
})
